import { UseMutationOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { type ResponseConfig } from "~/backend/axiosClient"
import { queryClient } from "~/backend/queryClient"
import {
  findIngestionSettingsQueryKey,
  useUpdateIngestionSettings,
} from "~/gen/api/hooks"
import { IdResponse, UpdateIngestionSettingsRequestDto } from "~/gen/api/types"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const useUpdateIngestionSettingsMutation = (
  options?: UseMutationOptions<
    ResponseConfig<IdResponse>,
    unknown,
    { data: UpdateIngestionSettingsRequestDto }
  >
) => {
  const { t } = useTranslation("organizationSettings")
  return useUpdateIngestionSettings({
    mutation: {
      ...options,
      onSuccess: async (data, variables, onMutateResult, context) => {
        options?.onSuccess?.(data, variables, onMutateResult, context)
        showSuccessNotification({
          message: t("ingestionSettings.update.success"),
          title: t("ingestionSettings.update.success"),
        })
        await queryClient.invalidateQueries({
          queryKey: findIngestionSettingsQueryKey(),
        })
        await queryClient.invalidateQueries({
          queryKey: ["standards"],
        })
      },
      onError: (error, variables, onMutateResult, context) => {
        if (error instanceof AxiosError) {
          showAxiosError({
            notificationMessage: t("ingestionSettings.update.error", {
              error: error.response?.data ?? error.message,
            }),
            showNotification: true,
            error,
          })
        }
        options?.onError?.(error, variables, onMutateResult, context)
      },
    },
  })
}
